/**
 * MentalCapacityProcessModal – state-specific guardianship and administration process information.
 * Used from Client Profile when user needs guidance on mental capacity and legal documentation.
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';

interface StateProcess {
    tribunal: string;
    process: string;
    timeline: string;
    costs: string;
    website: string;
    contact: string;
}

interface MentalCapacityProcessModalProps {
    onClose: () => void;
}

const MentalCapacityProcessModal = ({ onClose }: MentalCapacityProcessModalProps) => {
    const [selectedState, setSelectedState] = useState('');

    const stateProcesses: Record<string, StateProcess> = {
        'NSW': {
            tribunal: 'NSW Civil and Administrative Tribunal (NCAT)',
            process: 'Apply for guardianship and/or financial management orders through NCAT. You will need medical evidence confirming lack of capacity, an application form, and supporting documentation. The tribunal will review your application and may hold a hearing.',
            timeline: 'Typically 3-6 months from application to order',
            costs: 'Application fees apply (may be waived in hardship cases). Legal representation recommended but not mandatory.',
            website: 'https://www.ncat.nsw.gov.au/',
            contact: '1300 006 228'
        },
        'VIC': {
            tribunal: 'Victorian Civil and Administrative Tribunal (VCAT)',
            process: 'Apply for guardianship and/or administration orders through VCAT. Requires medical assessments, application forms, and evidence of the need for an order. The tribunal will assess whether the person needs a guardian/administrator.',
            timeline: 'Generally 2-4 months from application to hearing',
            costs: 'Application fees apply. You may need to pay for medical assessments. Legal costs vary.',
            website: 'https://www.vcat.vic.gov.au/',
            contact: '1300 018 228'
        },
        'QLD': {
            tribunal: 'Queensland Civil and Administrative Tribunal (QCAT)',
            process: 'Apply for guardianship and/or administration orders. Medical evidence is essential. QCAT will assess if the person has impaired capacity and whether an order is necessary to protect their interests.',
            timeline: 'Usually 3-5 months from application',
            costs: 'Application fees apply. Medical assessment costs and potential legal fees.',
            website: 'https://www.qcat.qld.gov.au/',
            contact: '1300 753 228'
        },
        'WA': {
            tribunal: 'State Administrative Tribunal (SAT)',
            process: 'Apply for guardianship and/or administration orders through SAT. Requires medical reports, application documentation, and evidence that an order is needed.',
            timeline: 'Approximately 3-6 months',
            costs: 'Application fees and potential costs for medical assessments and legal representation.',
            website: 'https://www.sat.justice.wa.gov.au/',
            contact: '(08) 9219 3111'
        },
        'SA': {
            tribunal: 'South Australian Civil and Administrative Tribunal (SACAT)',
            process: 'Apply for guardianship and/or administration orders. Medical evidence confirming lack of capacity is required. SACAT will determine if orders are necessary and appropriate.',
            timeline: 'Typically 2-4 months',
            costs: 'Application fees apply. Medical assessment and potential legal costs.',
            website: 'https://www.sacat.sa.gov.au/',
            contact: '1800 723 767'
        },
        'TAS': {
            tribunal: 'Tasmanian Civil and Administrative Tribunal (TASCAT)',
            process: 'Apply for guardianship and/or administration orders through TASCAT. Medical evidence and application forms required. The tribunal will assess the need for orders.',
            timeline: 'Generally 2-4 months',
            costs: 'Fees may apply. Medical assessment costs and potential legal fees.',
            website: 'https://www.tascat.tas.gov.au/',
            contact: '(03) 6165 7517'
        },
        'ACT': {
            tribunal: 'ACT Civil and Administrative Tribunal (ACAT)',
            process: 'Apply for guardianship and/or management orders. Medical assessments are essential. ACAT will review the application and make orders if appropriate.',
            timeline: 'Typically 2-4 months',
            costs: 'Application fees apply. Medical assessment costs and potential legal fees.',
            website: 'https://www.acat.act.gov.au/',
            contact: '(02) 6207 1740'
        },
        'NT': {
            tribunal: 'Northern Territory Civil and Administrative Tribunal (NTCAT)',
            process: 'Apply for guardianship and/or administration orders through NTCAT. Medical evidence confirming impaired capacity is required. The tribunal will assess the application.',
            timeline: 'Generally 2-4 months',
            costs: 'Fees may apply. Medical assessment and potential legal costs.',
            website: 'https://ntcat.nt.gov.au/',
            contact: '(08) 8999 0064'
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Mental Capacity & Legal Documentation Process</h2>
                        <p className="text-sm text-gray-600 mt-1">State-specific information for guardianship and administration orders</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-all duration-300 hover:scale-110"
                    >
                        <X size={24} />
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select your state or territory:
                        </label>
                        <select
                            value={selectedState}
                            onChange={(e) => setSelectedState(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Choose a state or territory...</option>
                            {Object.keys(stateProcesses).map(state => (
                                <option key={state} value={state}>{state}</option>
                            ))}
                        </select>
                    </div>

                    {selectedState && stateProcesses[selectedState] && (
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">{selectedState} - Guardianship & Administration Process</h3>
                            
                            <div className="space-y-4">
                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Tribunal:</h4>
                                    <p className="text-gray-700">{stateProcesses[selectedState].tribunal}</p>
                                </div>
                                
                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Application Process:</h4>
                                    <p className="text-gray-700">{stateProcesses[selectedState].process}</p>
                                </div>
                                
                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Typical Timeline:</h4>
                                    <p className="text-gray-700">{stateProcesses[selectedState].timeline}</p>
                                </div>
                                
                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Costs:</h4>
                                    <p className="text-gray-700">{stateProcesses[selectedState].costs}</p>
                                </div>
                                
                                <div className="border-t border-blue-200 pt-4 mt-4">
                                    <h4 className="font-semibold text-gray-800 mb-2">Important Next Steps:</h4>
                                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                                        <li>Gather medical evidence confirming lack of mental capacity (from GP, geriatrician, or psychiatrist)</li>
                                        <li>Contact the tribunal to obtain application forms and understand requirements</li>
                                        <li>Consider consulting with a solicitor specializing in elder law and guardianship</li>
                                        <li>Prepare supporting documentation demonstrating the need for orders</li>
                                        <li>Submit application and attend any required hearings</li>
                                    </ul>
                                </div>
                                
                                <div className="bg-white p-4 rounded-lg border border-blue-200 mt-4">
                                    <h4 className="font-semibold text-gray-800 mb-2">Contact Information:</h4>
                                    <p className="text-gray-700 mb-2">
                                        <strong>Website:</strong> <a href={stateProcesses[selectedState].website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{stateProcesses[selectedState].website}</a>
                                    </p>
                                    <p className="text-gray-700">
                                        <strong>Phone:</strong> {stateProcesses[selectedState].contact}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {!selectedState && (
                        <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg text-center">
                            <p className="text-gray-600">Please select your state or territory to view specific information about the guardianship and administration process.</p>
                        </div>
                    )}

                    <div className="mt-6 p-4 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg">
                        <h4 className="font-semibold text-amber-800 mb-2">⚠️ Important Considerations:</h4>
                        <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
                            <li>This process can be lengthy, stressful, and costly for families</li>
                            <li>If the person still has capacity, they should create a Will and EPOA immediately</li>
                            <li>Legal advice is strongly recommended before applying to tribunals</li>
                            <li>Each state has specific requirements and processes - this is general information only</li>
                            <li>Consider seeking support from elder law solicitors or advocacy services</li>
                        </ul>
                    </div>
                </div>
                
                <div className="border-t border-gray-200 p-4 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MentalCapacityProcessModal;
